// ====================================================================
// CHUNKS — data-driven, streamed, per-chunk instanced world props
// ====================================================================
// The design plan's world architecture, applied to the existing village:
//
//   - World content is DATA: addProp(type, descriptor) registers plain
//     records ({x, z, scale, ...}); nothing touches the GPU until the chunk
//     builds. The current village emits these from its procedural layout
//     code; authored zones will ship the same records as chunk JSON — the
//     loader below won't change.
//   - Props route to a 30-unit chunk grid. Each chunk builds its own
//     InstancedMesh per prop type with a computed bounding sphere and
//     frustumCulled = true — the old single world-spanning InstancedMesh had
//     to disable culling entirely.
//   - Chunks stream: built within LOAD_RADIUS of the player, torn down (GPU
//     buffers disposed; geometry/materials are shared and stay) beyond
//     UNLOAD_RADIUS, at most one build per call so startup and streaming
//     never spike a frame. The plain-data records persist — a chunk can
//     rebuild forever.
//
// Collision is registered separately (collision.js) and does not stream;
// see the note there.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { LOW, ULTRA_LOW_SHADOWS } from '../core/tier.js';
import { TUNING } from '../core/tuning.js';
import { PALETTE } from '../core/palette.js';
import { heightAt, WORLD_HALF } from './heightfield.js';
import { bakeColor, tintVertexRange, coloredBox } from './geoColor.js';
import { kitGeometry, kitMaterial, KIT_KINDS } from './kit.js';
import {
  OCCLUSION_FADE_DISABLED, FADE_MIN, FADE_INSTANCE_MARGIN, segDistSq, easeFade,
} from './occlusionFade.js';

// 40-unit chunks put the whole village (±9) inside the centre chunk, so the
// always-on diorama view pays for one chunk's meshes, not four. Smaller
// chunks only start winning when the world outgrows the view radius — re-tune
// when zones land.
export const CHUNK_SIZE = 40;
// WORLD_HALF is imported from heightfield.js (the sampling authority) so the map
// half-extent has a single source.
const LOAD_RADIUS = TUNING.chunkLoadRadius ?? 58;          // build when closer (chunk centre)
const UNLOAD_RADIUS = TUNING.chunkUnloadRadius ?? 66;      // tear down when farther (hysteresis)
const UNLOAD_RADIUS_SQ = UNLOAD_RADIUS * UNLOAD_RADIUS;

// One shared geometry + material per prop type, reused by every chunk.
// compose() writes a descriptor's instance transform into v/q/s.
const _e = new THREE.Euler();

// bakeColor (flat) and tintVertexRange (bottom→top gradient) live in geoColor.js.

// The default instance transform: sit on the ground, yaw about Y, uniform scale.
// Shared by every prop type that doesn't need a custom vertical offset. Stays
// allocation-free (reuses the module-scope _e) — it runs per instance on build.
function composeYawScalar(d, v, q, s) {
  v.set(d.x, d.gy, d.z);
  _e.set(0, d.yaw, 0);
  q.setFromEuler(_e);
  s.setScalar(d.scale);
}

const PROP_TYPES = {
  // trunk + foliage are fadeable: a tree is the classic "weird vision block" for
  // the high diorama camera, and the canopy isn't even a collision obstacle so
  // the pull-in never saw it. Fading trunk + foliage together ghosts the whole
  // tree coherently. The `centerY` of each fadeable type (its render-centre
  // height above gy) feeds the per-instance occlusion test in updateOcclusionFade.
  trunk: {
    geo: () => tintVertexRange(new THREE.CylinderGeometry(0.16, 0.16, 0.9, LOW ? 6 : 10), PALETTE.woodDark, PALETTE.woodLight),
    vertexColors: true, castShadow: !ULTRA_LOW_SHADOWS, receiveShadow: true,
    fadeable: true, centerY: 0.45, fadeRadius: 0.22,
    compose(d, v, q, s) { v.set(d.x, d.gy + 0.45 * d.scale, d.z); s.setScalar(d.scale); },
  },
  foliage: {
    geo: () => tintVertexRange(new THREE.SphereGeometry(0.55, LOW ? 6 : 10, LOW ? 4 : 8), PALETTE.leafDark, PALETTE.leafTip),
    color: PALETTE.leafBase, vertexColors: true, perInstanceColor: true, castShadow: !ULTRA_LOW_SHADOWS, sway: true,
    fadeable: true, centerY: 0.9, fadeRadius: 0.7,
    compose(d, v, q, s) { const leafScale = d.leafScale ?? d.scale; v.set(d.x, d.gy + (d.leafLift ?? 0.9) * d.scale, d.z); s.setScalar(leafScale); },
  },
  bush: {
    geo: () => tintVertexRange(new THREE.SphereGeometry(0.4, LOW ? 5 : 8, LOW ? 3 : 6), PALETTE.meadowDark, PALETTE.sageSoft),
    vertexColors: true, color: PALETTE.sage, castShadow: !LOW,
    fadeable: true, centerY: 0.3, fadeRadius: 0.44,
    compose(d, v, q, s) { v.set(d.x, d.gy + 0.3 * d.scale, d.z); s.setScalar(d.scale); },
  },

  berryBush: {
    geo: () => {
      const base = tintVertexRange(new THREE.SphereGeometry(0.4, LOW ? 5 : 8, LOW ? 3 : 6), PALETTE.meadowDark, PALETTE.sageSoft);
      const berries = [];
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2 + 0.25;
        const b = bakeColor(new THREE.SphereGeometry(0.045, 5, 4), i % 2 ? PALETTE.coral : PALETTE.wordRed);
        b.translate(Math.cos(a) * 0.23, 0.34 + (i % 2) * 0.05, Math.sin(a) * 0.23);
        berries.push(b);
      }
      return mergeGeometries([base, ...berries], false);
    },
    vertexColors: true, color: PALETTE.sage, castShadow: !LOW,
    fadeable: true, centerY: 0.3, fadeRadius: 0.44,
    compose(d, v, q, s) { v.set(d.x, d.gy + 0.3 * d.scale, d.z); s.setScalar(d.scale); },
  },
  starAccent: {
    geo: () => {
      const parts = [];
      for (let i = 0; i < 4; i++) {
        const ray = bakeColor(new THREE.BoxGeometry(0.08, 0.018, 0.42), PALETTE.goldSoft);
        ray.rotateY((i / 4) * Math.PI);
        ray.translate(0, 0.018, 0);
        parts.push(ray);
      }
      const core = bakeColor(new THREE.SphereGeometry(0.075, 6, 4), PALETTE.gold);
      core.translate(0, 0.04, 0);
      return mergeGeometries([...parts, core], false);
    },
    vertexColors: true, castShadow: false, receiveShadow: false,
    compose(d, v, q, s) { v.set(d.x, d.gy + 0.035, d.z); _e.set(0, d.yaw, 0); q.setFromEuler(_e); s.setScalar(d.scale); },
  },
  rock: {
    geo: () => {
      const g = new THREE.DodecahedronGeometry(0.34, 0);
      const pos = g.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        pos.setXYZ(i, pos.getX(i) * 1.12, pos.getY(i) * 0.56, pos.getZ(i) * 0.9);
      }
      g.computeVertexNormals();
      return tintVertexRange(g, PALETTE.stoneDark, PALETTE.stoneLight);
    },
    vertexColors: true, perInstanceColor: true, color: PALETTE.stone, castShadow: !LOW, receiveShadow: true,
    compose(d, v, q, s) { v.set(d.x, d.gy + 0.18 * d.scale, d.z); _e.set(0, d.yaw, 0); q.setFromEuler(_e); s.setScalar(d.scale); },
  },
  pathStone: {
    geo: () => tintVertexRange(new THREE.CylinderGeometry(1, 1, 0.05, LOW ? 6 : 10), PALETTE.stoneDark, PALETTE.stoneLight),
    vertexColors: true, perInstanceColor: true, color: PALETTE.stone, receiveShadow: true,
    compose(d, v, q, s) { v.set(d.x, d.gy + 0.025, d.z); s.set(d.r, 1, d.r); },
  },
  fencePost: {
    geo: () => new THREE.BoxGeometry(0.08, 0.6, 0.08),
    color: PALETTE.wood, castShadow: !LOW,
    compose(d, v, q, s) { v.set(d.x, d.gy + 0.3, d.z); s.setScalar(1); },
  },
  fenceRail: {
    // Unit length along X, stretched per instance — every rail in a chunk is
    // one draw call instead of one mesh each.
    geo: () => new THREE.BoxGeometry(1, 0.05, 0.05),
    color: PALETTE.wood,
    compose(d, v, q, s) { v.set(d.x, d.gy + 0.45, d.z); _e.set(0, d.yaw, 0); q.setFromEuler(_e); s.set(d.len, 1, 1); },
  },
  mushroom: {
    // Cream stem + red cap merged with baked vertex colors: one material,
    // one draw per chunk for the whole crop.
    geo: () => {
      const stem = bakeColor(new THREE.CylinderGeometry(0.05, 0.07, 0.16, 5), PALETTE.cream);
      stem.translate(0, 0.08, 0);
      const cap = bakeColor(new THREE.ConeGeometry(0.16, 0.15, 7), PALETTE.coral);
      cap.translate(0, 0.22, 0);
      return mergeGeometries([stem, cap], false);
    },
    vertexColors: true, castShadow: false,
    compose: composeYawScalar,
  },
  flowerStem: {
    geo: () => new THREE.CylinderGeometry(0.012, 0.018, 1, 4),
    color: PALETTE.meadowDark,
    compose(d, v, q, s) { v.set(d.x, d.gy + d.h / 2, d.z); s.set(1, d.h, 1); },
  },
  flowerHead: {
    geo: () => {
      const parts = [];
      const center = bakeColor(new THREE.SphereGeometry(0.035, 6, 4), 0xfff2c4);
      parts.push(center);
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2;
        const p = bakeColor(new THREE.SphereGeometry(0.04, 5, 4), 0xffffff);
        p.scale(1.25, 0.55, 0.7);
        p.translate(Math.cos(a) * 0.055, 0, Math.sin(a) * 0.055);
        parts.push(p);
      }
      const g = mergeGeometries(parts, false);
      // Cream centre and soft petal lift are vertex-baked, so the whole flower
      // head remains one instanced draw call with no texture lookup.
      tintVertexRange(g, 0xf2d07a, 0xffffff);
      for (const p of parts) p.dispose();
      return g;
    },
    color: 0xffffff, perInstanceColor: true,   // white base × instanceColor
    compose(d, v, q, s) { v.set(d.x, d.gy + d.h, d.z); s.setScalar(d.s); },
  },
  lilyPad: {
    geo: () => {
      const pad = bakeColor(new THREE.CylinderGeometry(0.18, 0.18, 0.018, LOW ? 8 : 12, 1, false, 0.28, Math.PI * 1.72), 0x5f9a58);
      const notch = bakeColor(new THREE.BoxGeometry(0.08, 0.016, 0.16), 0x4f8a3c);
      notch.translate(0.08, 0, 0);
      return mergeGeometries([pad, notch], false);
    },
    vertexColors: true, castShadow: false, receiveShadow: false,
    compose(d, v, q, s) { v.set(d.x, d.gy + 0.045, d.z); _e.set(0, d.yaw, 0); q.setFromEuler(_e); s.setScalar(d.scale); },
  },
  // A tuft of slender blades fanning up from the ground. A handful of thin
  // boxes merged with a baked dark-base→bright-tip vertical gradient, so the
  // whole crop is one material / one draw per chunk (the mushroom trick). No
  // map → the toroidal-tiling rule doesn't apply. Kept static (no sway) so it
  // costs nothing per frame.
  grassTuft: {
    geo: () => {
      const blades = [];
      const n = LOW ? 3 : 5;
      for (let i = 0; i < n; i++) {
        const h = 0.26 + (i % 3) * 0.07;
        const yaw = (i / n) * Math.PI * 2 + i * 1.7;
        const b = new THREE.BoxGeometry(0.028, h, 0.012);
        b.translate(0, h / 2, 0);
        b.rotateX(0.16 + (i % 2) * 0.12);        // lean outward
        b.rotateY(yaw);
        b.translate(Math.cos(yaw) * 0.03, 0, Math.sin(yaw) * 0.03);
        blades.push(b);
      }
      const g = mergeGeometries(blades, false);
      return tintVertexRange(g, PALETTE.meadowDark, PALETTE.meadowLight);
    },
    vertexColors: true, castShadow: false, receiveShadow: false,
    compose: composeYawScalar,
  },
  crate: {
    geo: () => {
      const body = coloredBox(0.48, 0.42, 0.48, 0, 0.21, 0, PALETTE.woodLight);
      const bandColor = PALETTE.woodDark;
      const bands = [];
      for (const z of [-0.25, 0.25]) bands.push(coloredBox(0.56, 0.09, 0.045, 0, 0.24, z, bandColor));
      for (const x of [-0.25, 0.25]) bands.push(coloredBox(0.045, 0.09, 0.56, x, 0.24, 0, bandColor));
      return mergeGeometries([body, ...bands], false);
    },
    vertexColors: true, castShadow: !LOW, receiveShadow: true,
    compose: composeYawScalar,
  },
  barrel: {
    geo: () => {
      const body = bakeColor(new THREE.CylinderGeometry(0.23, 0.23, 0.52, LOW ? 8 : 12), PALETTE.wood);
      body.translate(0, 0.26, 0);
      const hoopA = bakeColor(new THREE.CylinderGeometry(0.245, 0.245, 0.045, LOW ? 8 : 12), PALETTE.woodDark);
      hoopA.translate(0, 0.13, 0);
      const hoopB = hoopA.clone();
      hoopB.translate(0, 0.26, 0);
      return mergeGeometries([body, hoopA, hoopB], false);
    },
    vertexColors: true, castShadow: !LOW, receiveShadow: true,
    compose: composeYawScalar,
  },
  mailbox: {
    geo: () => {
      const post = coloredBox(0.08, 0.55, 0.08, 0, 0.275, 0, 0x6b4423);
      const box = coloredBox(0.42, 0.24, 0.28, 0, 0.66, 0, 0x5c8fb8);
      const flag = coloredBox(0.045, 0.23, 0.12, 0.24, 0.76, 0, 0xd84a3a);
      return mergeGeometries([post, box, flag], false);
    },
    vertexColors: true, castShadow: !LOW, receiveShadow: true,
    compose: composeYawScalar,
  },
  flowerBox: {
    geo: () => {
      const wood = coloredBox(0.62, 0.18, 0.22, 0, 0.09, 0, PALETTE.wood);
      const leaves = coloredBox(0.56, 0.08, 0.18, 0, 0.22, 0, 0x4f8a3c);
      const blooms = [];
      for (let i = 0; i < 3; i++) {
        const bloom = bakeColor(new THREE.SphereGeometry(0.055, 6, 4), [0xff5d8f, 0xffd23f, 0x9b6bff][i]);
        bloom.translate((i - 1) * 0.18, 0.3, (i % 2 ? -0.03 : 0.04));
        blooms.push(bloom);
      }
      return mergeGeometries([wood, leaves, ...blooms], false);
    },
    vertexColors: true, castShadow: false, receiveShadow: true,
    compose: composeYawScalar,
  },
  tinyFlag: {
    geo: () => {
      const pole = bakeColor(new THREE.CylinderGeometry(0.018, 0.018, 0.72, 5), 0x6b4423);
      pole.translate(0, 0.36, 0);
      const flag = coloredBox(0.34, 0.2, 0.025, 0.17, 0.56, 0, 0xf0d36a);
      return mergeGeometries([pole, flag], false);
    },
    vertexColors: true, castShadow: false,
    compose: composeYawScalar,
  },
  marketBasket: {
    geo: () => {
      const basket = coloredBox(0.46, 0.18, 0.32, 0, 0.09, 0, 0xb8844a);
      const handle = bakeColor(new THREE.TorusGeometry(0.19, 0.018, 5, LOW ? 8 : 12, Math.PI), 0x6b4423);
      handle.rotateZ(Math.PI);
      handle.scale(1, 0.8, 1);
      handle.translate(0, 0.2, 0);
      const goods = [];
      for (let i = 0; i < 4; i++) {
        const g = bakeColor(new THREE.SphereGeometry(0.07, 6, 4), [0xd84a3a, 0xf0d36a, 0x6a9540, 0xe06f55][i]);
        g.translate((i - 1.5) * 0.1, 0.22, (i % 2 - 0.5) * 0.12);
        goods.push(g);
      }
      return mergeGeometries([basket, handle, ...goods], false);
    },
    vertexColors: true, castShadow: false, receiveShadow: true,
    compose: composeYawScalar,
  },
  // ---- handmade kit props (world/kit.js) ----------------------------------
  // Geometry + one shared atlas material come from kit.glb (loaded before the
  // first chunk builds — main.js top-level awaits it). The `material` factory
  // overrides the Lambert the loop below would otherwise create; all four
  // types share the ONE kit material, so a chunk with lanterns and every
  // platform size costs four draws but a single texture bind. If the kit
  // failed to load, geo() falls back to a wood-toned box of the same footprint
  // and material() returns null → the vertexColors Lambert path takes over.
  // compose honours d.y (absolute Y — floating placements) over the sampled
  // ground height, matching how obby-style callers position pads by their top.
  ...Object.fromEntries(['lantern', 'platform_small', 'platform_mid', 'platform_tall',
    'platform_floating', 'maypole', 'bunny_hutch', 'haybale'].map((kind) => [kind, {
    geo: () => {
      const g = kitGeometry(kind);
      if (g) return g;
      const fb = KIT_KINDS[kind].fallback;
      if (kind === 'maypole') {
        const pole = bakeColor(new THREE.CylinderGeometry(0.045, 0.055, fb.h, 6), PALETTE.woodDark);
        pole.translate(0, fb.h / 2, 0);
        const flag = bakeColor(new THREE.BoxGeometry(0.38, 0.16, 0.025), PALETTE.coral);
        flag.translate(0.22, fb.h * 0.82, 0);
        return mergeGeometries([pole, flag], false);
      }
      if (kind === 'bunny_hutch') {
        const body = bakeColor(new THREE.BoxGeometry(fb.w, fb.h * 0.72, fb.d), PALETTE.wood);
        body.translate(0, fb.h * 0.36, 0);
        const roof = bakeColor(new THREE.BoxGeometry(fb.w * 1.08, fb.h * 0.2, fb.d * 1.08), PALETTE.roofBrown);
        roof.translate(0, fb.h * 0.82, 0);
        return mergeGeometries([body, roof], false);
      }
      const fallbackColor = kind === 'haybale' ? PALETTE.gold : PALETTE.wood;
      const box = bakeColor(new THREE.BoxGeometry(fb.w, fb.h, fb.d), fallbackColor);
      box.translate(0, fb.h / 2, 0);
      return box;
    },
    material: () => kitMaterial(),
    vertexColors: true,                     // only relevant to the fallback box
    castShadow: !LOW, receiveShadow: true,
    compose(d, v, q, s) { v.set(d.x, d.y ?? d.gy, d.z); _e.set(0, d.yaw || 0, 0); q.setFromEuler(_e); s.setScalar(d.scale ?? 1); },
  }])),
};
// Lazily-built shared resources
for (const t of Object.values(PROP_TYPES)) {
  t.sharedGeo = null;
  t.sharedMat = null;
}

const chunks = new Map();   // "i,j" -> { ci, cj, cx, cz, data: {type: []}, group, sway: [] }
let parentGroup = null;
let totalProps = 0;
// Stable prop ids: procedural props get 'p<n>' (stable across reloads because
// the build order is deterministic); editor-placed props get 'e<n>'. The edit
// layer (world/editLayer.js) keys deletions/additions off these. If procedural
// emission order ever changes, bump the edit-layer schema version to drop stale
// ids rather than misapply them.
let _pid = 0, _eid = 0;

function reservePropId(id) {
  const m = /^(p|e)(\d+)$/.exec(String(id || ''));
  if (!m) return;
  const next = Number(m[2]) + 1;
  if (m[1] === 'p') _pid = Math.max(_pid, next);
  else _eid = Math.max(_eid, next);
}

function chunkFor(x, z) {
  const ci = Math.floor((x + WORLD_HALF) / CHUNK_SIZE);
  const cj = Math.floor((z + WORLD_HALF) / CHUNK_SIZE);
  const key = ci + ',' + cj;
  let c = chunks.get(key);
  if (!c) {
    c = {
      key, ci, cj,
      cx: -WORLD_HALF + (ci + 0.5) * CHUNK_SIZE,   // centre
      cz: -WORLD_HALF + (cj + 0.5) * CHUNK_SIZE,
      data: {}, group: null, sway: null, fadeMeshes: null,
    };
    chunks.set(key, c);
  }
  return c;
}

// Register a prop. Ground height is sampled once here — descriptors are
// authored relative to the ground, wherever it is.
export function addProp(type, d) {
  d.gy = heightAt(d.x, d.z);
  // Normalize the transform fields every compose() reads. A record authored
  // without a yaw or scale would otherwise feed undefined into _e.set()/
  // setScalar() → a NaN quaternion/matrix that also poisons the chunk's
  // bounding sphere (frustum culling then drops the whole chunk). Kit props
  // guard these inline; doing it once here makes every prop type safe.
  if (d.yaw == null) d.yaw = 0;
  if (d.scale == null) d.scale = 1;
  if (d.phase == null) d.phase = 0;   // sway attribute reads it; NaN would poison aSway
  if (!d.id) d.id = 'p' + (_pid++);
  else reservePropId(d.id);
  const c = chunkFor(d.x, d.z);
  (c.data[type] || (c.data[type] = [])).push(d);
  totalProps++;
}

// ---- Editor live-edit API (authoring only) ------------------------------
// addProp registers a record; these add the "make it appear/disappear now and
// rebuild only the affected chunk" behavior the editor needs. A chunk rebuild
// is an unloadChunk + buildChunk (sub-millisecond, user-paced) — far simpler
// than surgically editing one InstancedMesh, and it rebuilds the per-instance
// attributes (aFade, aSway) consistently.

function rebuildChunk(c) {
  if (c.group) { unloadChunk(c); buildChunk(c); }
}

// Place a prop and show it immediately. Editor props get an 'e' id so the edit
// layer can tell them apart from procedural ones.
export function addPropLive(type, d) {
  if (!d.id) d.id = 'e' + (_eid++);
  else reservePropId(d.id);
  addProp(type, d);
  rebuildChunk(chunkFor(d.x, d.z));
  return d.id;
}

// Remove the nearest prop to (x, z) within maxDist; returns { type, d } or null.
export function removePropNear(x, z, maxDist = 1.5, typeFilter = null) {
  let bestD = maxDist * maxDist, bestC = null, bestType = null, bestIdx = -1;
  for (const c of chunks.values()) {
    for (const type in c.data) {
      if (typeFilter && type !== typeFilter) continue;
      const list = c.data[type];
      for (let i = 0; i < list.length; i++) {
        const dd = (list[i].x - x) ** 2 + (list[i].z - z) ** 2;
        if (dd < bestD) { bestD = dd; bestC = c; bestType = type; bestIdx = i; }
      }
    }
  }
  if (!bestC) return null;
  const [d] = bestC.data[bestType].splice(bestIdx, 1);
  totalProps--;
  rebuildChunk(bestC);
  return { type: bestType, d };
}

// Remove by stable id; returns { type, d } or null.
export function removePropById(id) {
  for (const c of chunks.values()) {
    for (const type in c.data) {
      const i = c.data[type].findIndex(d => d.id === id);
      if (i >= 0) {
        const [d] = c.data[type].splice(i, 1);
        totalProps--;
        rebuildChunk(c);
        return { type, d };
      }
    }
  }
  return null;
}

export function rebuildChunkAt(x, z) { rebuildChunk(chunkFor(x, z)); }

// Move an existing live descriptor, keeping it in the correct chunk bucket.
// Editor-only; used by the unified object editor when dragging chunk props.
export function movePropRecord(type, d, oldX, oldZ) {
  const oldC = chunkFor(oldX, oldZ);
  const newC = chunkFor(d.x, d.z);
  if (oldC !== newC) {
    const oldList = oldC.data[type] || [];
    const i = oldList.indexOf(d);
    if (i >= 0) oldList.splice(i, 1);
    (newC.data[type] || (newC.data[type] = [])).push(d);
  }
  rebuildChunk(oldC);
  if (newC !== oldC) rebuildChunk(newC);
}

// Flat list of every live prop record, optional filter(type, d) → boolean.
export function enumerateProps(filter = null) {
  const out = [];
  for (const c of chunks.values())
    for (const type in c.data)
      for (const d of c.data[type])
        if (!filter || filter(type, d)) out.push({ type, d });
  return out;
}

// Re-sample ground height for every prop (call after a terrain sculpt so props
// don't float) and rebuild loaded chunks. Editor-only; user-paced.
export function resettleProps() {
  for (const c of chunks.values()) {
    for (const type in c.data)
      for (const d of c.data[type]) d.gy = heightAt(d.x, d.z);
    rebuildChunk(c);
  }
}

export function attachChunksTo(group) { parentGroup = group; }

const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(),
      _v = new THREE.Vector3(), _s = new THREE.Vector3(), _col = new THREE.Color();

function freezeStaticObject(o) {
  // Chunks are rebuilt explicitly when edited/streamed; they never animate via
  // Object3D transforms. Opt them out of three.js' recursive matrix walk so
  // Chromebook CPUs spend their frame budget on gameplay, not re-validating
  // hundreds of static world nodes.
  o.updateMatrix();
  o.matrixAutoUpdate = false;
}

// Patch a shared prop material so a per-instance `aFade` attribute scales its
// final alpha — the InstancedMesh "ghost". Same onBeforeCompile string-replace
// the grass material uses (main.js), so it's proven across no-build/dev/prod.
// transparent stays on for the type, but with aFade defaulting to 1 the mesh
// draws opaque until updateOcclusionFade dials an occluding instance down. Note
// the attribute is declared ONLY in the vertex shader (attributes are illegal
// in fragment shaders); the fragment side gets the varying.
function patchFadeMaterial(mat) {
  mat.transparent = true;
  // depthWrite stays true: ghosted instances are a handful, and writing depth
  // keeps the whole canopy from sorting against the world every frame. If halos
  // appear on the ghosts, revisit per-mesh depthWrite toggling.
  const prevOBC = mat.onBeforeCompile;
  mat.onBeforeCompile = (shader) => {
    if (prevOBC) prevOBC(shader);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float aFade;\nvarying float vFade;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvFade = aFade;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying float vFade;')
      .replace('#include <opaque_fragment>', '#include <opaque_fragment>\ngl_FragColor.a *= vFade;');
  };
  mat.needsUpdate = true;
}

// Foliage sway runs in the vertex shader: a shared time uniform plus a
// per-instance vec2 attribute (phase, amplitude factor) reproduce the old CPU
// path's world-space offsets without recomposing and re-uploading a chunk's
// whole instanceMatrix every frame. That upload was the reason sway used to be
// mid+ only; as a vertex offset it is cheap enough to run on every tier,
// including LOW. Base instance matrices stay fully static.
// Wrap at 20π — the smallest t where both 1.4t and 1.1t are 2π multiples, so
// the modulo never pops the phase and float32 precision holds all session.
const SWAY_TIME_WRAP = 20 * Math.PI;
const _swayUniform = { value: 0 };   // shared by reference across all program variants

export function updateSwayTime(t) { _swayUniform.value = t % SWAY_TIME_WRAP; }

// Test/debug introspection: the shared sway clock plus how many loaded meshes
// carry the per-instance aSway attribute (worldtest asserts time advances and
// count > 0, the headless-deterministic proof that GPU sway is wired up).
export function readSwayState() {
  let meshes = 0;
  for (const c of chunks.values()) {
    if (!c.group) continue;
    for (const ch of c.group.children) {
      if (ch.isInstancedMesh && ch.geometry.getAttribute('aSway')) meshes++;
    }
  }
  return { time: _swayUniform.value, meshes };
}

function patchSwayMaterial(mat) {
  const prevOBC = mat.onBeforeCompile;
  mat.onBeforeCompile = (shader) => {
    if (prevOBC) prevOBC(shader);
    shader.uniforms.uSwayTime = _swayUniform;
    // The offset is applied in `transformed` space, BEFORE the instanceMatrix
    // (whose scale is leafScale) multiplies it — aSway.y carries the
    // scale/leafScale correction so the world-space amplitude is 0.04·scale,
    // exactly what the CPU path produced.
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec2 aSway;\nuniform float uSwayTime;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n'
        + 'transformed.x += sin( uSwayTime * 1.4 + aSway.x ) * 0.04 * aSway.y;\n'
        + 'transformed.z += cos( uSwayTime * 1.1 + aSway.x ) * 0.03 * aSway.y;');
  };
  mat.needsUpdate = true;
}

function buildChunk(c) {
  if (!parentGroup) throw new Error('attachChunksTo(group) must be called before updateChunkStreaming()');
  c.group = new THREE.Group();
  freezeStaticObject(c.group);
  let count = 0;
  for (const [type, list] of Object.entries(c.data)) {
    if (!list.length) continue;
    const t = PROP_TYPES[type];
    t.sharedGeo ||= t.geo();
    // A type may bring its own material (kit props share the atlas material);
    // a null factory result falls through to the standard Lambert.
    t.sharedMat ||= (t.material && t.material()) || new THREE.MeshLambertMaterial(
      t.vertexColors ? { vertexColors: true } : { color: t.color }
    );
    // Fadeable and swaying types need per-INSTANCE data, which lives on the
    // geometry as instanced attributes — so they can't share one geometry
    // across chunks. Clone it per chunk (the geos are tiny: a sphere/cylinder
    // of ~tens of verts) and dispose on unload. Shared materials are patched
    // once per type; fade + sway patches chain through prevOBC safely.
    const fadeable = !!t.fadeable;
    const perInstanceAttrs = fadeable || !!t.sway;
    const geo = perInstanceAttrs ? t.sharedGeo.clone() : t.sharedGeo;
    if (perInstanceAttrs) (c.clonedGeos ||= []).push(geo);
    if (fadeable && !t._fadePatched) { patchFadeMaterial(t.sharedMat); t._fadePatched = true; }
    if (t.sway && !t._swayPatched) { patchSwayMaterial(t.sharedMat); t._swayPatched = true; }
    const mesh = new THREE.InstancedMesh(geo, t.sharedMat, list.length);
    freezeStaticObject(mesh);
    mesh.castShadow = !!t.castShadow;
    mesh.receiveShadow = !!t.receiveShadow;
    for (let i = 0; i < list.length; i++) {
      _q.identity();
      t.compose(list[i], _v, _q, _s);
      _m.compose(_v, _q, _s);
      mesh.setMatrixAt(i, _m);
      if (t.perInstanceColor) mesh.setColorAt(i, _col.set(list[i].color ?? t.color ?? 0xffffff));
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    if (fadeable) {
      const arr = new Float32Array(list.length).fill(1);   // 1 = fully opaque
      const attr = new THREE.InstancedBufferAttribute(arr, 1);
      attr.setUsage(THREE.DynamicDrawUsage);
      geo.setAttribute('aFade', attr);
      (c.fadeMeshes ||= []).push({ centerY: t.centerY || 0, fadeRadius: t.fadeRadius || 0.6, list, attr });
    }
    if (t.sway) {
      // Static per-instance sway inputs: x = phase, y = amplitude factor. The
      // factor is scale/leafScale because the instanceMatrix scales the offset
      // by leafScale afterwards (see patchSwayMaterial).
      const arr = new Float32Array(list.length * 2);
      for (let i = 0; i < list.length; i++) {
        const d = list[i], ls = d.leafScale ?? d.scale ?? 1;
        arr[i * 2] = d.phase || 0;
        arr[i * 2 + 1] = ls > 0 ? (d.scale ?? 1) / ls : 1;
      }
      geo.setAttribute('aSway', new THREE.InstancedBufferAttribute(arr, 2));
    }
    // Per-chunk bounds → real frustum culling, the thing the old
    // world-spanning instanced meshes could never have.
    mesh.computeBoundingSphere();
    mesh.frustumCulled = true;
    c.group.add(mesh);
    count += list.length;
  }
  parentGroup.add(c.group);
  if (LOW) console.log(`[world] chunk ${c.key} built (${count} props)`);
}

function unloadChunk(c) {
  if (!parentGroup) throw new Error('attachChunksTo(group) must be called before unloading chunks');
  parentGroup.remove(c.group);
  c.group.traverse((o) => { if (o.isInstancedMesh) o.dispose(); });   // instance buffers only
  // Per-chunk cloned geometries (fadeable/sway types) are NOT shared — dispose them.
  if (c.clonedGeos) { for (const g of c.clonedGeos) g.dispose(); }
  c.group = null;
  c.clonedGeos = null;
  c.fadeMeshes = null;
  if (LOW) console.log(`[world] chunk ${c.key} unloaded`);
}

// Streaming: hysteresis ring around the player, ≤1 build per call so a chunk
// build can never stack up into a frame spike. Call from the main loop at a
// few Hz, and in a tight loop at boot until it returns false (nothing to do).
export function updateChunkStreaming(px, pz, loadRadius = LOAD_RADIUS) {
  let builtOne = false;
  const loadRadiusSq = loadRadius * loadRadius;
  for (const c of chunks.values()) {
    const dx = px - c.cx, dz = pz - c.cz;
    const dSq = dx * dx + dz * dz;
    if (!c.group && dSq < loadRadiusSq && !builtOne) {
      buildChunk(c);
      builtOne = true;
    } else if (c.group && dSq > UNLOAD_RADIUS_SQ) {
      unloadChunk(c);
    }
  }
  return builtOne;
}

// Diorama occlusion fade: ghost any fadeable instance (trees, bushes) that
// falls on the line between the camera eye and Chocky's head, so the high, STILL
// camera never gets a vision block and never has to dolly in (the dolly was the
// "swing"). Pure scalar math per instance — the village is ~one chunk, so this
// is tens of tests/frame and adds ZERO draw calls. A chunk's aFade buffer is
// only re-uploaded on the frames an instance actually changes, so standing in
// the open uploads nothing. Pass the FINAL camera position and Chocky's head.
let _fadeUploads = 0;
export function updateOcclusionFade(camX, camY, camZ, lookX, lookY, lookZ, dt) {
  _fadeUploads = 0;
  if (OCCLUSION_FADE_DISABLED) return;
  for (const c of chunks.values()) {
    if (!c.fadeMeshes) continue;
    for (const fm of c.fadeMeshes) {
      const { list, attr, centerY, fadeRadius } = fm;
      const arr = attr.array;
      let dirty = false;
      for (let i = 0; i < list.length; i++) {
        const d = list[i];
        const iy = d.gy + centerY * d.scale;
        const distSq = segDistSq(camX, camY, camZ, lookX, lookY, lookZ, d.x, iy, d.z);
        const r = fadeRadius * (d.leafScale ?? d.scale ?? 1) + FADE_INSTANCE_MARGIN;
        const target = distSq < r * r ? FADE_MIN : 1;
        const cur = arr[i];
        if (cur === target) continue;
        const next = easeFade(cur, target, dt);
        // Settle exactly on the target once imperceptibly close, so idle frames
        // hit the `cur === target` fast-path above and upload nothing.
        arr[i] = Math.abs(next - target) < 0.004 ? target : next;
        dirty = true;
      }
      if (dirty) { attr.needsUpdate = true; _fadeUploads++; }
    }
  }
}

// Test-only introspection (exposed through window.__chockyDebug in no-build):
// the deepest ghost across loaded fadeable instances, how many are faded, and
// how many buffers uploaded last frame (0 when idle proves no wasted work).
export function readOcclusionFadeState() {
  let minInstanceFade = 1, fadedCount = 0, instances = 0;
  for (const c of chunks.values()) {
    if (!c.fadeMeshes) continue;
    for (const fm of c.fadeMeshes) {
      const arr = fm.attr.array;
      for (let i = 0; i < arr.length; i++) {
        instances++;
        if (arr[i] < minInstanceFade) minInstanceFade = arr[i];
        if (arr[i] < 0.95) fadedCount++;
      }
    }
  }
  return { minInstanceFade, fadedCount, instances, lastUploads: _fadeUploads };
}

export function chunkStats() {
  let loaded = 0;
  for (const c of chunks.values()) if (c.group) loaded++;
  return { chunks: chunks.size, loaded, props: totalProps };
}
